import { useEffect, useState, useCallback } from 'react';
import { api, type Me, type EmailReportSender, type EmailReportDomain, type EmailReportAuditRow, type EmailReportSchedule } from '../lib/api';

const inputCls = 'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

const COMMANDS = `WXCC QUEUE <name> <range>        live queue report (CSV attached)
WXCC AGENT <name> <range>        agent report
WXCC TEAM <name> <range>         team report
WXC DID <number> <range>         calling detail for a phone number
TEMPLATE <Team> DID <10digit> QUEUES <q1,q2,...>
                                 full 16-report Analyzer template pack (JSON)
SCHEDULE daily|weekly <command> [TO a@b.com,...]
LIST · CANCEL <id> · HELP

Ranges: today | yesterday | last24h | last7d | from YYYY-MM-DD to YYYY-MM-DD`;

function outcomePill(o: string) {
  const cls = o === 'accepted' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : o === 'help' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
    : o.startsWith('denied') ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
    : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{o}</span>;
}

export default function EmailReporting({ me }: { me: Me }) {
  const isSuper = me.role === 'super_admin';
  const isAdmin = isSuper || me.role === 'tenant_admin';

  const [senders, setSenders] = useState<EmailReportSender[] | null>(null);
  const [domains, setDomains] = useState<EmailReportDomain[] | null>(null);
  const [auditRows, setAuditRows] = useState<EmailReportAuditRow[] | null>(null);
  const [schedules, setSchedules] = useState<EmailReportSchedule[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [nEmail, setNEmail] = useState('');
  const [nName, setNName] = useState('');
  const [nRole, setNRole] = useState('supervisor');
  const [nTenant, setNTenant] = useState('');
  const [nDomain, setNDomain] = useState('');
  const [nDomTenant, setNDomTenant] = useState('');

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [s, d, a, sc] = await Promise.all([
        api.emailReportSenders(), api.emailReportDomains(), api.emailReportAudit(50), api.emailReportSchedules(),
      ]);
      setSenders(s.senders); setDomains(d.domains); setAuditRows(a.audit); setSchedules(sc.schedules);
    } catch (e: any) { setErr(e.message); }
  }, []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  async function addSender() {
    if (!nEmail.trim()) { setErr('Enter an email address.'); return; }
    setBusy(true); setErr(null); setMsg(null);
    try {
      const body: any = { email: nEmail.trim(), role: nRole, display_name: nName.trim() || undefined };
      if (isSuper && nTenant.trim()) body.tenant_id = nTenant.trim();
      await api.emailReportSenderAdd(body);
      setMsg(`Authorized ${nEmail.trim()} ✔`); setNEmail(''); setNName('');
      await load();
    } catch (e: any) { setErr(`Add failed: ${e.message}`); }
    finally { setBusy(false); }
  }
  async function disableSender(email: string) {
    if (!window.confirm(`Revoke email reporting access for ${email}?`)) return;
    setBusy(true); setErr(null);
    try { await api.emailReportSenderDisable(email); await load(); }
    catch (e: any) { setErr(`Revoke failed: ${e.message}`); }
    finally { setBusy(false); }
  }
  async function addDomain() {
    if (!nDomain.trim()) { setErr('Enter a domain, e.g. santamonica.gov'); return; }
    setBusy(true); setErr(null); setMsg(null);
    try {
      const body: any = { domain: nDomain.trim() };
      if (isSuper && nDomTenant.trim()) body.tenant_id = nDomTenant.trim();
      await api.emailReportDomainAdd(body);
      setMsg(`Authorized domain ${nDomain.trim()} ✔`); setNDomain('');
      await load();
    } catch (e: any) { setErr(`Add failed: ${e.message}`); }
    finally { setBusy(false); }
  }
  async function disableDomain(domain: string) {
    if (!window.confirm(`Revoke email reporting access for everyone @${domain}?`)) return;
    setBusy(true); setErr(null);
    try { await api.emailReportDomainDisable(domain); await load(); }
    catch (e: any) { setErr(`Revoke failed: ${e.message}`); }
    finally { setBusy(false); }
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 text-center text-gray-500">
        Email Reporting administration requires a tenant admin or super admin role.
      </div>
    );
  }

  const th = 'px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400';
  const td = 'px-3 py-2 text-sm';

  return (
    <div className="w-full px-4 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">WxC/WxCC Email Services — Access</h1>
        <p className="text-sm text-gray-500">
          Authorized senders &amp; domains below govern <strong>both</strong> email services, tenant-scoped by sender (SET AND FORGET · TNH-KB-0076):
        </p>
        <ul className="mt-1 text-sm text-gray-500 list-disc pl-5">
          <li><span className="font-mono">reports@trinethub.com</span> — email a request, get a live report or Analyzer template pack.</li>
          <li><span className="font-mono">support@trinethub.com</span> — email an issue, get a ticket number + AI first-line triage (Operations → Support Tickets).</li>
        </ul>
        <p className="mt-1 text-xs text-gray-400">One list, both services: authorize a person or a whole domain once here and they can use reports <em>and</em> support.</p>
      </div>

      {err && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">{err}</div>}
      {msg && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-300">{msg}</div>}

      {/* Commands cheat-sheet */}
      <details className="rounded-xl border border-tnh-border dark:border-tnh-border-dark bg-tnh-card dark:bg-tnh-card-dark p-4">
        <summary className="cursor-pointer font-semibold text-sm">📧 Email commands (what users can send)</summary>
        <pre className="mt-3 text-xs whitespace-pre-wrap font-mono text-gray-600 dark:text-gray-300">{COMMANDS}</pre>
      </details>

      {/* Authorized senders */}
      <div className="rounded-xl border border-tnh-border dark:border-tnh-border-dark bg-tnh-card dark:bg-tnh-card-dark p-4">
        <h2 className="font-semibold mb-1">Authorized senders</h2>
        <p className="text-xs text-gray-500 mb-3">Exact-email authorizations for <strong>reports@ and support@</strong> — these override domain rules and can carry elevated roles.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <input className={`${inputCls} flex-1 min-w-[220px]`} placeholder="user@customer.gov" value={nEmail} onChange={(e) => setNEmail(e.target.value)} />
          <input className={`${inputCls} w-44`} placeholder="Display name" value={nName} onChange={(e) => setNName(e.target.value)} />
          <select className={inputCls} value={nRole} onChange={(e) => setNRole(e.target.value)}>
            <option value="supervisor">supervisor</option>
            <option value="tenant_admin">tenant_admin</option>
            {isSuper && <option value="super_admin">super_admin</option>}
          </select>
          {isSuper && <input className={`${inputCls} w-36`} placeholder="tenant_id" value={nTenant} onChange={(e) => setNTenant(e.target.value)} />}
          <button onClick={addSender} disabled={busy} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">Authorize</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-tnh-border dark:border-tnh-border-dark">
              <th className={th}>Email</th>{isSuper && <th className={th}>Tenant</th>}<th className={th}>Role</th><th className={th}>Status</th><th className={th}></th>
            </tr></thead>
            <tbody>
              {(senders ?? []).map((s) => (
                <tr key={s.email} className="border-b border-gray-100 dark:border-gray-800">
                  <td className={td}><span className="font-mono">{s.email}</span>{s.display_name ? <span className="text-gray-400 ml-2">{s.display_name}</span> : null}</td>
                  {isSuper && <td className={td}>{s.tenant_id}</td>}
                  <td className={td}>{s.role}</td>
                  <td className={td}>{s.enabled ? <span className="text-emerald-600">enabled</span> : <span className="text-gray-400">revoked</span>}</td>
                  <td className={`${td} text-right`}>
                    {s.enabled ? (
                      <button onClick={() => disableSender(s.email)} disabled={busy} className="text-xs text-red-600 hover:underline">Revoke</button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {senders && senders.length === 0 && <tr><td className={`${td} text-gray-400`} colSpan={5}>No authorized senders yet.</td></tr>}
              {!senders && <tr><td className={`${td} text-gray-400`} colSpan={5}>Loading…</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Authorized domains */}
      <div className="rounded-xl border border-tnh-border dark:border-tnh-border-dark bg-tnh-card dark:bg-tnh-card-dark p-4">
        <h2 className="font-semibold mb-1">Authorized domains</h2>
        <p className="text-xs text-gray-500 mb-3">
          Everyone at the domain is served their tenant automatically — e.g. <span className="font-mono">@longbeach.gov</span> → Long Beach,{' '}
          <span className="font-mono">@santamonica.gov</span> → Santa Monica. Spoofing is blocked by the DMARC/SPF+DKIM gate.
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          <input className={`${inputCls} flex-1 min-w-[200px]`} placeholder="customer.gov" value={nDomain} onChange={(e) => setNDomain(e.target.value)} />
          {isSuper && <input className={`${inputCls} w-36`} placeholder="tenant_id" value={nDomTenant} onChange={(e) => setNDomTenant(e.target.value)} />}
          <button onClick={addDomain} disabled={busy} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">Authorize domain</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-tnh-border dark:border-tnh-border-dark">
              <th className={th}>Domain</th><th className={th}>Tenant</th><th className={th}>Status</th><th className={th}></th>
            </tr></thead>
            <tbody>
              {(domains ?? []).map((d) => (
                <tr key={d.domain} className="border-b border-gray-100 dark:border-gray-800">
                  <td className={td}><span className="font-mono">@{d.domain}</span></td>
                  <td className={td}>{d.tenant_id}</td>
                  <td className={td}>{d.enabled ? <span className="text-emerald-600">enabled</span> : <span className="text-gray-400">revoked</span>}</td>
                  <td className={`${td} text-right`}>
                    {d.enabled ? <button onClick={() => disableDomain(d.domain)} disabled={busy} className="text-xs text-red-600 hover:underline">Revoke</button> : null}
                  </td>
                </tr>
              ))}
              {domains && domains.length === 0 && <tr><td className={`${td} text-gray-400`} colSpan={4}>No authorized domains yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Schedules */}
      <div className="rounded-xl border border-tnh-border dark:border-tnh-border-dark bg-tnh-card dark:bg-tnh-card-dark p-4">
        <h2 className="font-semibold mb-3">Active schedules</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-tnh-border dark:border-tnh-border-dark">
              <th className={th}>#</th><th className={th}>Owner</th><th className={th}>Cadence</th><th className={th}>Command</th><th className={th}>Recipients</th><th className={th}>Next run</th><th className={th}>Last status</th>
            </tr></thead>
            <tbody>
              {(schedules ?? []).filter((s) => s.enabled).map((s) => (
                <tr key={s.id} className="border-b border-gray-100 dark:border-gray-800">
                  <td className={td}>{s.id}</td>
                  <td className={`${td} font-mono text-xs`}>{s.owner_email}</td>
                  <td className={td}>{s.cadence}</td>
                  <td className={`${td} font-mono text-xs`}>{s.command}</td>
                  <td className={`${td} font-mono text-xs`}>{s.recipients}</td>
                  <td className={td}>{new Date(s.next_run_at).toLocaleString()}</td>
                  <td className={td}>{s.last_status ?? '—'}</td>
                </tr>
              ))}
              {schedules && schedules.filter((s) => s.enabled).length === 0 && <tr><td className={`${td} text-gray-400`} colSpan={7}>No active schedules. Users create them by emailing: SCHEDULE daily WXCC QUEUE &lt;name&gt; yesterday</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Audit */}
      <div className="rounded-xl border border-tnh-border dark:border-tnh-border-dark bg-tnh-card dark:bg-tnh-card-dark p-4">
        <div className="flex items-center mb-3">
          <h2 className="font-semibold">Recent requests (audit)</h2>
          <button onClick={load} className="ml-auto text-xs text-blue-600 hover:underline">Refresh</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-tnh-border dark:border-tnh-border-dark">
              <th className={th}>Received</th><th className={th}>Sender</th><th className={th}>Command</th><th className={th}>Outcome</th>
            </tr></thead>
            <tbody>
              {(auditRows ?? []).map((a, i) => (
                <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                  <td className={`${td} whitespace-nowrap text-xs text-gray-500`}>{a.received_at?.replace('T', ' ').slice(0, 19)}</td>
                  <td className={`${td} font-mono text-xs`}>{a.sender}</td>
                  <td className={`${td} font-mono text-xs`} title={a.detail ?? ''}>{a.command || a.subject}</td>
                  <td className={td}>{outcomePill(a.outcome)}</td>
                </tr>
              ))}
              {auditRows && auditRows.length === 0 && <tr><td className={`${td} text-gray-400`} colSpan={4}>No requests yet — send HELP to reports@trinethub.com to test.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
